import React, { useState } from 'react';
import './StupidSystemDesigns.css';

const splitwiseDesignCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

using tpl3 = tuple<int, int, double>;     // <debitId, creditId, amount>

// Global counters
int userIdCounter = 1;
int groupIdCounter = 1;
int actionIdCounter = 1; 

class User {
    int userId;
    string username;
    double balance;
public:
    User(string name) {
        userId = userIdCounter++;
        username = name;
        balance = 0;
    }
    
    int getUserId() { return userId; }
    string getUsername() { return username; }
    double getBalance() { return balance; }
    
    // Allow O(1) global balance updates
    void updateBalance(double amt) { balance += amt; }
};

// Struct to hold the Append-Only Log (History)
struct ActionRecord {
    int actionId;
    string type;      // "EXPENSE", "PAYBACK", or "REVERT"
    int groupId;
    int actorId;      // The user who paid
    int targetId;     // The user receiving payback (-1 for standard expenses)
    double amount;
};

// expenses map acts strictly as a History AOL
unordered_map<int, ActionRecord> expenses; 

class Group {
    int groupId;
    string groupName;
    int createdByUserId;
    unordered_set<int> members;

public:
    Group(string name, int userId) {
        groupId = groupIdCounter++;
        groupName = name;
        createdByUserId = userId;
        members = {userId};
    }
    void addUser(int userId) { members.insert(userId); }
    void removeUser(int userId) { members.erase(userId); }
    
    int getGroupId() { return groupId; }
    string getGroupName() { return groupName; }
    unordered_set<int>& getMembers() { return members; }
};

class Ledger {
    unordered_map<int, vector<tpl3>> ledgerMap;
public:
    Ledger() {}
    
    void addTransaction(int groupId, int debitUser, int creditUser, double amount) {
        // Add & then simplify transactions.
        ledgerMap[groupId].push_back({debitUser, creditUser, amount});
        simplify(groupId);
    }
    
    void simplify(int groupId) {
        vector<tpl3> oldTransactions = ledgerMap[groupId];
        unordered_map<int, double> netBalanceMap = {};
        
        for(auto [debitId, creditId, amt] : oldTransactions) {
            netBalanceMap[debitId] -= amt;
            netBalanceMap[creditId] += amt;
        }
        
        priority_queue<pair<double, int>> debitPQ, creditPQ;
        for(auto &pairs : netBalanceMap) {
            if(pairs.second < -0.001) { 
                debitPQ.push({-pairs.second, pairs.first});
            } else if(pairs.second > 0.001) {
                creditPQ.push({pairs.second, pairs.first});
            }
        }
        
        vector<tpl3> newTransactions = {};
        while(!creditPQ.empty() && !debitPQ.empty()) {
            int creditId = creditPQ.top().second;
            double creditBal = creditPQ.top().first;
            int debitId = debitPQ.top().second;
            double debitBal = debitPQ.top().first;
            
            creditPQ.pop(); debitPQ.pop();
            double minBal = min(creditBal, debitBal);
            newTransactions.push_back({debitId, creditId, minBal});
            
            creditBal -= minBal;
            debitBal -= minBal;
            
            if(creditBal > 0.001) creditPQ.push({creditBal, creditId});
            if(debitBal > 0.001) debitPQ.push({debitBal, debitId});
        }
        // ledgerMap ONLY ever holds the simplified transactions! 
        // Deltas are erased and replaced by the simplified state.
        ledgerMap[groupId] = newTransactions;
    }
    
    vector<tpl3> getTransactions(int groupId) {
        return ledgerMap[groupId];
    }
};

class SplitController {
    unordered_map<int, User*> users;
    unordered_map<int, Group*> groups;
    Ledger *ledger = NULL;

public:
    SplitController() {
        users = {};
        ledger = new Ledger();
        userIdCounter = 1;
        groupIdCounter = 1;
        actionIdCounter = 1;
    }
    
    ~SplitController() {
        delete ledger;
        for (auto& pair : users) delete pair.second;
        for (auto& pair : groups) delete pair.second;
    }
    
    int addUser(string username) {
        User *user = new User(username);
        int uId = user->getUserId();
        users[uId] = user; 
        return uId;
    }
    
    int createGroup(int userId, string name) {
        Group *group = new Group(name, userId);
        int gId = group->getGroupId();
        groups[gId] = group;   
        return gId;
    }
    
    void addUserToGroup(int userId, int groupId) {
        groups[groupId]->addUser(userId);
    }
    
    int addFriend(int userId1, int userId2) {
        string groupName = "1-1 (" + users[userId1]->getUsername() + " & " + users[userId2]->getUsername() + ")";
        Group *group = new Group(groupName, userId1);
        group->addUser(userId2);
        int gId = group->getGroupId();
        groups[gId] = group;
        return gId;
    }
    
    void addTransaction(int groupId, int debitId, int creditId, double amount) {
        users[debitId]->updateBalance(-amount);
        users[creditId]->updateBalance(amount);
        ledger->addTransaction(groupId, debitId, creditId, amount);
    }
    
    int addExpense(int groupId, int userId, double amount) {
        unordered_set<int> members = groups[groupId]->getMembers();
        if(members.empty()) return -1;
        
        double splitAmount = amount / members.size();
        int actId = actionIdCounter++;
        
        // Push to AOL
        expenses[actId] = {actId, "EXPENSE", groupId, userId, -1, amount}; 
        
        for(int memberId : members) {
            if(memberId != userId) {
                addTransaction(groupId, memberId, userId, splitAmount);
            }
        }
        return actId;
    }
    
    void revertExpense(int expenseId) {
        if(expenses.find(expenseId) == expenses.end()) return;
        ActionRecord orig = expenses[expenseId];
        
        if (orig.type != "EXPENSE") {
            cout << "Can only revert root EXPENSE records directly.\n";
            return;
        }
        
        int groupId = orig.groupId;
        int paidByUserId = orig.actorId;
        double amount = orig.amount;
        
        unordered_set<int> members = groups[groupId]->getMembers();
        double splitAmount = amount / members.size();
        
        for(int memberId : members) {
            if(memberId != paidByUserId) {
                addTransaction(groupId, paidByUserId, memberId, splitAmount);
            }
        }
        
        // Append REVERT event to AOL (DO NOT erase the original, maintaining the History Log)
        int actId = actionIdCounter++;
        expenses[actId] = {actId, "REVERT", groupId, paidByUserId, expenseId, amount};
    }
    
    void payBack(int groupId, int debitUserId, int creditUserId, double amount) {
        // Push to AOL
        int actId = actionIdCounter++;
        expenses[actId] = {actId, "PAYBACK", groupId, debitUserId, creditUserId, amount};
        
        // debitUser pays back creditUser. 
        // creditUser gets cash, so they "owe" debitUser on the app to reduce the debt.
        addTransaction(groupId, creditUserId, debitUserId, amount);
        cout << users[debitUserId]->getUsername() << " paid $" << amount 
             << " to " << users[creditUserId]->getUsername() << " in '" 
             << groups[groupId]->getGroupName() << "'\n";
    }

    void printUserDashboard(int userId) {
        User* u = users[userId];
        cout << "\n============================================\n";
        cout << " DASHBOARD FOR: " << u->getUsername() << "\n";
        cout << " TOTAL NET BALANCE: $" << u->getBalance() << "\n";
        cout << "--------------------------------------------\n";
        
        bool hasPending = false;
        
        for(auto& [groupId, group] : groups) {
            if(group->getMembers().count(userId)) {
                vector<tpl3> txs = ledger->getTransactions(groupId);
                
                for(auto [debitId, creditId, amt] : txs) {
                    if(debitId == userId) {
                        cout << "[ " << group->getGroupName() << " ] You owe " 
                             << users[creditId]->getUsername() << " $" << amt << "\n";
                        hasPending = true;
                    } 
                    else if (creditId == userId) {
                        cout << "[ " << group->getGroupName() << " ] " 
                             << users[debitId]->getUsername() << " owes you $" << amt << "\n";
                        hasPending = true;
                    }
                }
            }
        }
        
        if(!hasPending) cout << "No pending transactions!\n";
        cout << "============================================\n";
    }
    
    void printAOLHistory() {
        cout << "\n=========== EXPENSES HISTORY (AOL) ===========\n";
        for (int i = 1; i < actionIdCounter; i++) {
            if (expenses.count(i)) {
                auto& rec = expenses[i];
                cout << "ID: " << rec.actionId << " | TYPE: " << rec.type 
                     << " | Grp: " << rec.groupId << " | Actor: " << users[rec.actorId]->getUsername();
                if (rec.targetId != -1) cout << " | Target: " << (rec.type == "REVERT" ? to_string(rec.targetId) : users[rec.targetId]->getUsername());
                cout << " | Amount: $" << rec.amount << "\n";
            }
        }
        cout << "==============================================\n";
    }
};

int main() {
    SplitController controller;
    
    // 1. Setup Users
    int uAlice = controller.addUser("Alice");
    int uBob = controller.addUser("Bob");
    int uCharlie = controller.addUser("Charlie");
    int uDavid = controller.addUser("David");
    
    // 2. Setup Group & Friendships
    int gApartment = controller.createGroup(uAlice, "Apartment");
    controller.addUserToGroup(uBob, gApartment);
    controller.addUserToGroup(uCharlie, gApartment);
    
    int gFriendship = controller.addFriend(uAlice, uDavid); // 1-1
    
    // 3. Deep Testing Actions
    cout << "--- ACTIONS ---\n";
    cout << "1. Alice pays $90 for Apartment (Alice, Bob, Charlie)\n";
    controller.addExpense(gApartment, uAlice, 90.0);
    
    cout << "2. Bob pays $30 for Apartment (Alice, Bob, Charlie)\n";
    controller.addExpense(gApartment, uBob, 30.0);
    
    cout << "3. Alice pays $100 for Dinner with David (1-1 Friendship)\n";
    controller.addExpense(gFriendship, uAlice, 100.0);
    
    // Let's check everyone's global states!
    
    // ALICE:
    // Apt: Paid $90 (owes 30, gets 60). Apt net: +$50.
    // 1-1: Paid $100 (owes 50, gets 50). 1-1 net: +$50.
    // Alice Total Net: +$100.
    controller.printUserDashboard(uAlice);
    
    // BOB:
    // Apt net: -$10.
    // SIMPLIFIED LEDGER routes debts to maximize flow, so Bob owes Alice $10 directly.
    controller.printUserDashboard(uBob);
    
    // CHARLIE:
    // Unsimplified: Owes $30 to Alice, $10 to Bob. 
    // SIMPLIFIED LEDGER: Reroutes so Charlie owes Alice $40 directly.
    // Charlie Total Net: -$40.
    controller.printUserDashboard(uCharlie);
    
    // DAVID:
    // 1-1: Owes Alice $50. Total Net: -$50.
    controller.printUserDashboard(uDavid);
    
    // 4. Testing Paybacks
    cout << "\n--- SETTLING UP ---\n";
    controller.payBack(gApartment, uCharlie, uAlice, 30.0);
    
    // Check Charlie and Alice after payback
    // Previously Charlie owed Alice $40 (Simplified). He pays $30. 
    // Therefore he should now owe Alice $10!
    controller.printUserDashboard(uCharlie); 
    
    // Alice's Total Net drops from $100 to $70 (she got cash back in hand)
    controller.printUserDashboard(uAlice);   

    // 5. Print out the pure Append-Only Log of all historical actions
    controller.printAOLHistory();

    return 0;
}
`;

const stupidSplitwiseDesignCode = 'Still working on it..';

function SplitwiseDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState(false);
    const [stupidCopied, setStupidCopied] = useState(false);

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(splitwiseDesignCode);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setCopied(false);
        }
    };

    const handleCopyStupidCode = async () => {
        try {
            await navigator.clipboard.writeText(stupidSplitwiseDesignCode);
            setStupidCopied(true);
            window.setTimeout(() => setStupidCopied(false), 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setStupidCopied(false);
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Splitwise Design</h1>
                            <p className="system-designs-subtitle">
                                Simplifies tracking shared expenses. In case you need it, download from your app store. (not sponsored!)
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li>Support user profiles and multi-member group creation.</li>
                                <li>Automatically create a 1-on-1 friendship when two users connect directly.</li>
                                <li>Record who paid, the total amount, and split an expense equally among group members.</li>
                                <li>Enable direct peer-to-peer paybacks that reduce outstanding debt.</li>
                                <li>Reverse expenses safely using a reference to the historical snapshot being undone.</li>
                                <li>Provide real-time dashboards for total net balances and pending simplified debts across active groups.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>System Architecture &amp; Data Model</h2>
                            <ul>
                                <li><strong>Append-Only Log (AOL):</strong> Store every raw <code>EXPENSE</code>, <code>PAYBACK</code>, and <code>REVERT</code> action in an immutable history log.</li>
                                <li>The append-only history provides complete auditability without overwriting previous actions.</li>
                                <li><strong>Derived Ledger:</strong> Maintain real-time net balances for each user within each group.</li>
                                <li>Simplified debts remain a dynamic computed view rather than static stored transactions, so they can always be rebuilt from the logs.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Core Algorithms &amp; Ledger Strategy</h2>
                            <ul>
                                <li><strong>Greedy Debt Simplification:</strong> Use min/max heaps, such as <code>priority_queue</code>, to match the largest debtors with the largest creditors.</li>
                                <li>Each heap match creates a transfer for the smaller remaining amount, minimizing the total number of cross-transactions needed to settle a group.</li>
                                <li><strong>O(1) Balance Tracking:</strong> Update global user balances immediately when an action is appended.</li>
                                <li>Keep fast state changes decoupled from heavier debt-simplification computations and dashboard refreshes.</li>
                            </ul>
                            <br></br>
                            NOTE: Getting group's transactions is easy with ledger now, if HLD i would shard DB by groupId.<br></br>
                            But getting a single user's all pending transactions is very expensive.<br></br>
                            Maybe store a different table for user's transactions and force update these for any group-ledger changes?<br></br>
                            It feels like double work during expense/payback/revert, but it would make the dashboard O(1) instead of O(n) for each group user is in.
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <p className="system-design-description">
                                Imagine a global ledger where the debt-simplification algorithm runs across every friendship and group, regardless of whether the users actually know one another.
                            </p>
                            <p className="system-design-description">
                                Alice knows Bob, Alice also knows Sank, but Bob and Sank do not know each other. Sank gives Alice $50, Alice gives Bob $50, and then Alice tells Sank to collect his money from Bob, who Sank DOES NOT EVEN KNOW!!!!!!!
                            </p>
                            <p className="system-design-description">
                                The system technically reduced the number of transactions, but it created an impossible social graph: Sank now has to chase a stranger for a debt created by a different friendship.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>User profile</li>
                                <li>Friendship and group membership</li>
                                <li>Group</li>
                                <li>Append-only action: <code>EXPENSE</code>, <code>PAYBACK</code>, or <code>REVERT</code></li>
                                <li>Derived group ledger and user net balance</li>
                                <li>Simplified debt transfer</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                                <button t="button" className="copy-code-btn" onClick={handleCopyCode}>
                                    {copied ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{splitwiseDesignCode}</code></pre>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Stupid System Code</h2>
                                <button type="button" className="copy-code-btn" onClick={handleCopyStupidCode}>
                                    {stupidCopied ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{stupidSplitwiseDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        Now tell me, is it split_WISE or split_NOT_SO_WISE ? Get it? AAAHHAHAAHHAHAHHAHA
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default SplitwiseDesign;
